import axiosInstance from "api/axiosInstance";

import type { BannerImageUploadResponse, HomeScreenSettingComponent, HomeScreenSettingComponentPayload, HomeScreenSettingItem, HomeScreenSettingItemPayload, HomeScreenSettingResponse, PopupItem, PopupListParams, PopupListResponse } from "api/types/marketing.types";

const HOME_BASE = "/server/buccl_main/api/v1/admin/home-screen-setting/";
const POPUP_BASE = "/server/buccl_main/api/v1/admin/popups/";

/** 홈 화면 설정 서비스 */
export const homeService = {
  /** 홈 화면 컴포넌트 목록 조회 (배너 + 추천 상품 등) */
  get: () => axiosInstance.get<HomeScreenSettingResponse>(HOME_BASE),

  /** 홈 화면 컴포넌트 단건 생성 (백엔드가 배열 body 를 요구하므로 단건도 배열로 감싸서 전송) */
  create: (payload: HomeScreenSettingComponentPayload) => axiosInstance.post<HomeScreenSettingResponse>(HOME_BASE, [payload]),

  /** 홈 화면 컴포넌트 단건 수정 */
  update: (componentId: number, payload: Partial<HomeScreenSettingComponentPayload>) => axiosInstance.patch<HomeScreenSettingComponent>(`${HOME_BASE}${componentId}/`, payload),

  /** 홈 화면 컴포넌트 단건 삭제 */
  remove: (componentId: number) => axiosInstance.delete(`${HOME_BASE}${componentId}/`),

  /** 특정 컴포넌트에 아이템 신규 생성 */
  createItem: (componentId: number, payload: HomeScreenSettingItemPayload) => {
    return axiosInstance.post<HomeScreenSettingItem>(`${HOME_BASE}${componentId}/items/`, payload);
  },

  /** 홈 화면 컴포넌트 아이템 단건 수정 (부분 PATCH — 백엔드가 기존값과 머지) */
  updateItem: (itemId: number, payload: Partial<HomeScreenSettingItemPayload>) => {
    return axiosInstance.patch<HomeScreenSettingItem>(`${HOME_BASE}items/${itemId}/`, payload);
  },

  /** 홈 화면 컴포넌트 아이템 단건 삭제 */
  removeItem: (itemId: number) => axiosInstance.delete(`${HOME_BASE}items/${itemId}/`),

  /**
   * 배너 이미지 업로드.
   *
   * POST /server/buccl_main/api/v1/upload-image/banner/
   * - 인증: IsAuthenticated (어드민)
   * - 요청: multipart/form-data, 필드명 `image` (단일 파일)
   * - 제약: jpg/jpeg/png/gif/webp, 30MB 이하
   * - 응답(201): { url, filename, size, message }
   *   응답 url 예시: https://buccl-frontdoor-...azurefd.net/.../banners/main/20df717719b4.png
   */
  uploadBannerImage: (file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return axiosInstance.post<BannerImageUploadResponse>("/server/buccl_main/api/v1/upload-image/banner/", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

/** 팝업 관리 서비스 */
export const popupService = {
  /** 팝업 목록 조회 */
  list: (params?: PopupListParams) => axiosInstance.get<PopupListResponse>(POPUP_BASE, { params }),

  /** 팝업 생성 (이미지 포함 - FormData) */
  create: (data: FormData) =>
    axiosInstance.post<PopupItem>(POPUP_BASE, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  /** 팝업 수정 (이미지 포함 가능 - FormData) */
  update: (id: number, data: FormData) =>
    axiosInstance.patch<PopupItem>(`${POPUP_BASE}${id}/`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  /** 팝업 삭제 */
  remove: (id: number) => axiosInstance.delete(`${POPUP_BASE}${id}/`),

  /** 팝업 활성/비활성 토글 */
  toggleActive: (id: number, isActive: boolean) => axiosInstance.patch<PopupItem>(`${POPUP_BASE}${id}/`, { is_active: isActive }),
};
